<!--
  第三个区域：期数时间轴 + 奖品展示图
  - 时间轴卡片：6 个节点（活动开始 + 五期），点击某一期切换下方展示
  - 展示图 gift-diaplay.png 里没有「第一期」文字，用定位盖上去
  - 第五期是超级大奖，选中时不展示奖品图
-->
<script lang="ts">
  import { _ as t, locale } from "svelte-i18n";
  import giftDisplayCn from "../assets/images/cn/gift-diaplay.png";
  import giftDisplayTc from "../assets/images/tc/gift-diaplay.png";
  import birthCake from "../assets/images/stage/cake.png";
  import bigGift from "../assets/images/stage/gift.png";

  /** 简体用 cn 图，其余（繁体 / 英文 / 韩文 / 站外默认）用 tc 图 */
  let giftDisplay = $derived($locale === "zh-cn" ? giftDisplayCn : giftDisplayTc);

  interface Stage {
    /** 年份 */
    year: string;
    /** 月.日 */
    day: string;
    /** 文案 i18n key */
    labelKey: string;
    /** 是否可点击切换（活动开始不参与切换） */
    selectable: boolean;
    /** 幸运奖名额 */
    luckyCount?: number;
    /** 周年纪念奖名额 */
    annivCount?: number;
  }

  const stages: Stage[] = [
    { year: "2026", day: "8.15", labelKey: "stage_start", selectable: false },
    {
      year: "2026",
      day: "9.2",
      labelKey: "stage_1",
      selectable: true,
      luckyCount: 11,
      annivCount: 25,
    },
    {
      year: "2026",
      day: "9.16",
      labelKey: "stage_2",
      selectable: true,
      luckyCount: 12,
      annivCount: 25,
    },
    {
      year: "2026",
      day: "9.30",
      labelKey: "stage_3",
      selectable: true,
      luckyCount: 12,
      annivCount: 25,
    },
    {
      year: "2026",
      day: "10.14",
      labelKey: "stage_4",
      selectable: true,
      luckyCount: 12,
      annivCount: 25,
    },
    // 末期是大抽奖：节点画礼盒 + 「超级大奖」，但同样可以切换展示
    {
      year: "2026",
      day: "10.22",
      labelKey: "stage_5",
      selectable: true,
      luckyCount: 12,
      annivCount: 25,
    },
  ];

  const lastIndex = stages.length - 1;

  /** 当前选中的期数，默认第一期 */
  let current = $state(1);

  function select(index: number) {
    if (!stages[index].selectable) return;
    current = index;
  }
</script>

<!-- 时间轴卡片：设计稿 343x111 / radius 16 / padding 12px 8px / gap 10px / #3953A3 -->
<div class="stage-card rounded-[16px] px-[21px] py-[12px]">
  <div class="relative flex items-start gap-[10px]">
    <!-- 整条横线只画一根，避免分段拼接处出现圆角接缝 -->
    <span class="stage-line"></span>

    {#each stages as stage, i (stage.labelKey)}
      <!-- 首列左对齐：方块要和「活动开始」的左边缘对齐，其余列居中 -->
      <button
        type="button"
        class="flex-1 min-w-0 flex flex-col {i === 0
          ? 'items-start'
          : 'items-center'}"
        onclick={() => select(i)}
      >
        <!-- 日期：选中态铺蓝色底图，末期是金色字 -->
        <span class="stage-date" class:is-final={i === lastIndex}>
          {#if i === current}
            <!-- 底图比文字块四周各大 1px，单独一层避免撑高布局 -->
            <span class="stage-date-bg"></span>
          {/if}
          <span class="stage-date-text">
            <span>{stage.year}</span>
            <span>{stage.day}</span>
          </span>
        </span>

        <!-- 节点：横线 + 方块，当前期上方带蛋糕，末期换成礼盒 -->
        <span class="relative block w-full h-[24px]">
          {#if i === lastIndex}
            <!-- 礼盒和「超级大奖」文案放同一层，文案用百分比定位，不受宽度/取整影响 -->
            <span class="stage-gift">
              <img src={bigGift} alt="" class="block w-full h-full" />
              <span class="stage-super">{$t("super_prize")}</span>
            </span>
          {:else if i === current}
            <!-- 当前期只画蛋糕，不画方块：不用靠蛋糕去盖，任何宽度下都不会露边 -->
            <img src={birthCake} alt="" class="stage-cake" />
          {:else}
            <!-- 已过的期是蓝色渐变方块，后面的是白色 -->
            <span
              class="stage-dot"
              class:is-start={i === 0}
              class:is-reached={i < current}
            ></span>
          {/if}
        </span>

        <!-- 文案 -->
        <span
          class="stage-label mt-[6px]"
          class:is-final={i === lastIndex}
          class:is-active={i === current}
        >
          {$t(stage.labelKey)}
        </span>
      </button>
    {/each}
  </div>
</div>

<!--
  奖品展示图（图内无文字，期数文案定位在毛笔笔画上方）
  图 736px 宽里卡片本体是 x 16~731（715px），要让卡片本体对齐上面 343px 的时间轴，
  整图就得放到 736/715*343 ≈ 353px，并左移 16/715*343 ≈ 8px
-->
{#if current === lastIndex}
  <!--
    末期是超级大奖，没有对应的奖品展示图。
    这里再抽掉 20px 高度，让 big-awards.png 在自身 -20px 的基础上继续上移 20px。
    留 1px 实高是为了避免空盒子自身上下 margin 折叠导致 -20px 失效；
    只作用于末期分支，其余期数的位置不受影响。
  -->
  <div class="h-[1px] mt-[-21px]"></div>
{:else}
  <div class="relative mt-[-11px] w-[353px] ml-[-8px]">
    <img src={giftDisplay} alt="" class="w-full block" />
    <p class="stage-display-title">{$t(stages[current].labelKey)}</p>

    <!-- 名额：图里两个角标都是空的，数字用文字盖上去 -->
    <span class="stage-quota is-lucky">
      {$t("winner_count", { values: { count: stages[current].luckyCount } })}
    </span>
    <span class="stage-quota is-anniv">
      {$t("winner_count", { values: { count: stages[current].annivCount } })}
    </span>
  </div>
{/if}

<style>
  .stage-card {
    background: #3953a3;
  }

  /*
    日期块（设计稿 37.76x38.72）尺寸固定，切换选中时布局不跳
    文字：14px / 600 / line-height 16px / 居中 / #FFFFFF / shadow 0 1px 2px #003EC7
  */
  .stage-date {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 37.76px;
    height: 38.72px;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0;
    text-align: center;
    color: #ffffff;
    text-shadow: 0 1px 2px #003ec7;
  }

  /* 选中态底图 data-bg.png（76x76@2x），四周各放大 1px */
  .stage-date-bg {
    position: absolute;
    top: -1px;
    right: -1px;
    bottom: -1px;
    left: -1px;
    background: url("../assets/images/stage/date-bg.png") no-repeat center;
    background-size: 100% 100%;
  }

  /* 文字层要在底图之上：两者都定位，靠 DOM 顺序压过去 */
  .stage-date-text {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* 末期（大抽奖）：金色字 */
  .stage-date.is-final {
    color: #ffd24a;
  }

  /*
    横线（设计稿）：高 4px / radius 30 / #FFFFFF / shadow 0 1px 2px #003EC7 50%
    整行只画一根：
    - top = 日期块 38.72 + 节点区 24 的一半
    - left = 首个方块中心 4.82px
    - right = 末列中心，列宽 = (100% - 5*10px)/6，所以是 (100% - 50px)/12
  */
  .stage-line {
    position: absolute;
    top: 50.72px;
    left: 4.82px;
    right: calc((100% - 50px) / 12);
    height: 4px;
    margin-top: -2px;
    border-radius: 30px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 62, 199, 0.5);
  }

  /*
    线上的方块（设计稿）：9.64x9.64 / radius 2 / border 1px #FFFFFF 内描边
    / rotation 45° / drop shadow 0 1px 2px #003EC7 60%
    未到达：填充 #FFFFFF；已到达：渐变 #005DFF → #25D3FF
    注：box-shadow 会跟着 rotate 一起转，9.64px + 2px 模糊下看不出方向差异
  */
  .stage-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9.64px;
    height: 9.64px;
    margin: -4.82px 0 0 -4.82px;
    border: 1px solid #ffffff;
    border-radius: 2px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 62, 199, 0.6);
    transform: rotate(45deg);
  }

  .stage-dot.is-reached {
    background: linear-gradient(180deg, #005dff 0%, #25d3ff 100%);
  }

  /* 首个方块贴左，中心落在「活动开始」左边缘上 */
  .stage-dot.is-start {
    left: 0;
    margin-left: 0;
  }

  /* 蛋糕 16x24：坐在横线上，底部压过线一点 */
  .stage-cake {
    position: absolute;
    left: 50%;
    bottom: 50%;
    width: 16px;
    height: 24px;
    margin-left: -8px;
    margin-bottom: -10px;
  }

  /*
    「超级大奖」文案：6px / 600 / #FFFFFF（设计稿）
    胶囊底在 biggift.png 里（61x61 图上红条 y 44~57，中心 y=50.5 → 高度的 82.8%）
    相对礼盒自身用百分比定位，任何屏宽都跟胶囊对齐，不会因取整偏移
  */
  .stage-super {
    position: absolute;
    left: 50%;
    top: 83.2%;
    font-size: 6px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0;
    color: #ffffff;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  }

  /* 末期礼盒：替代方块，同时作为「超级大奖」文案的定位容器 */
  .stage-gift {
    position: absolute;
    display: block;
    left: 50%;
    top: 50%;
    width: 31px;
    height: 31px;
    margin: -15px 0 0 -15px;
  }

  /* 期数文案（设计稿）：12px / 500 / line-height 20px / #FFFFFF */
  .stage-label {
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0;
    color: #ffffff;
    white-space: nowrap;
  }

  .stage-label.is-final {
    color: #ffd24a;
  }

  /*
    展示图上的期数文案
    位置按图内的毛笔笔画推算（笔画在 736x579 图上的范围是 x 49~186、y 125~143）
    用百分比定位，图片随容器缩放时位置不变
  */
  .stage-display-title {
    position: absolute;
    left: 8%;
    top: 14.5%;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0;
    color: #003ec7;
    white-space: nowrap;
  }

  /*
    奖品角标里的名额
    角标在 736x579 图上的位置：
    幸运奖（橙）x 274~358、y 165~215 → 中心 42.9% / 32.8%
    周年纪念奖（蓝）x 593~686 → 中心 86.9%，同一行高度
  */
  .stage-quota {
    position: absolute;
    top: 32.8%;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    color: #ffffff;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  }

  .stage-quota.is-lucky {
    left: 43.1%;
  }

  .stage-quota.is-anniv {
    left: 86.9%;
  }
</style>
